import { QueryClient } from '@tanstack/react-query'

import { AppError } from '@/lib/errors'

/**
 * TanStack Query configuration.
 *
 * The defaults here are tuned for archival data, which behaves very
 * differently from a social feed: a person's birth date changed in 1887
 * and will not change again while you are looking at it. Long stale times
 * are correct, and they are the reason navigating a large tree feels
 * instant on a slow connection.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Five minutes. Genealogical records are edited in bursts by one
      // person, not continuously by many.
      staleTime: 5 * 60 * 1000,
      // Keep unused data for half an hour: navigating back to a person
      // you looked at two minutes ago should not re-fetch.
      gcTime: 30 * 60 * 1000,

      retry: (failureCount, error) => {
        // Never retry an authorisation failure. RLS said no; asking three
        // more times will not change its mind, and it delays the sign-in
        // redirect the user actually needs.
        if (error instanceof AppError && !error.isRetryable) return false
        return failureCount < 2
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),

      // Refetching on every window focus is wrong here — it produces a
      // burst of requests every time someone alt-tabs while reading a
      // long transcription.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      // Mutations are never retried automatically. A retried "add child"
      // that actually succeeded the first time creates a duplicate person,
      // and duplicates are the most expensive error in this domain.
      retry: false,
    },
  },
})

/**
 * Query key factory.
 *
 * Every key in the application is built here. Hand-written key arrays
 * scattered across features are how cache invalidation quietly stops
 * working — one file says `['people', id]` and another says
 * `['person', id]`, and neither is wrong until you need to invalidate.
 *
 * Keys are family-scoped at the root so switching families cannot serve
 * one family's cached data to another.
 */
export const queryKeys = {
  all: ['legacy'] as const,

  families: () => [...queryKeys.all, 'families'] as const,
  family: (familyId: string) => [...queryKeys.families(), familyId] as const,

  people: (familyId: string) => [...queryKeys.family(familyId), 'people'] as const,
  // `unknown` rather than Record<string, unknown>: filter objects are
  // interfaces, and TypeScript interfaces have no index signature, so the
  // narrower type would reject every real caller.
  personList: (familyId: string, filters?: unknown) =>
    [...queryKeys.people(familyId), 'list', filters ?? {}] as const,
  person: (familyId: string, personId: string) =>
    [...queryKeys.people(familyId), personId] as const,
  personTimeline: (familyId: string, personId: string) =>
    [...queryKeys.person(familyId, personId), 'timeline'] as const,
  personFamilyGroup: (familyId: string, personId: string) =>
    [...queryKeys.person(familyId, personId), 'family-group'] as const,
  ancestors: (familyId: string, personId: string, generations: number) =>
    [...queryKeys.person(familyId, personId), 'ancestors', generations] as const,
  descendants: (familyId: string, personId: string, generations: number) =>
    [...queryKeys.person(familyId, personId), 'descendants', generations] as const,

  media: (familyId: string) => [...queryKeys.family(familyId), 'media'] as const,
  documents: (familyId: string) => [...queryKeys.family(familyId), 'documents'] as const,
  stories: (familyId: string) => [...queryKeys.family(familyId), 'stories'] as const,
  sources: (familyId: string) => [...queryKeys.family(familyId), 'sources'] as const,
  places: (familyId: string) => [...queryKeys.family(familyId), 'places'] as const,
  research: (familyId: string) => [...queryKeys.family(familyId), 'research'] as const,
  changeRequests: (familyId: string) => [...queryKeys.family(familyId), 'change-requests'] as const,
  members: (familyId: string) => [...queryKeys.family(familyId), 'members'] as const,

  search: (familyId: string, query: string) =>
    [...queryKeys.family(familyId), 'search', query] as const,

  session: () => [...queryKeys.all, 'session'] as const,
  profile: () => [...queryKeys.all, 'profile'] as const,
} as const
